import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    DetailScrollReveal,
    EditorialHeader,
    useIslandAutoplay,
} from '~/ui-rocco/pages/detail-shared';
import type { TeamMember } from '~/ui-rocco/pages/detail-shared';
import { formatSectionNumber } from '../utils/section-numbering';

interface ProfessionalTeamSectionProps {
    sectionNumber?: number;
}

/** One team-member slide — the TeamGrid tile (portrait, name, role). */
const TeamMemberSlide = ({ member }: { member: TeamMember }) => (
    <div className="flex flex-col">
        <div className="bg-neutral-200 relative aspect-[437/524] w-full overflow-hidden rounded-tile">
            {member.image ? (
                <img
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-[#181818] max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[1.375rem]">
            {member.name}
        </p>
        {member.jobTitle ? (
            <p className="m-0 font-sans leading-snug text-[#181818]/40 max-md:text-sm md:text-[1.375rem]">
                {member.jobTitle}
            </p>
        ) : null}
    </div>
);

/**
 * §6 — Team. "Who we are" header + the professional's public team members as a
 * four-column full-bleed carousel with Magic Island pagination, mirroring the
 * product page's About-the-Supplier products rail. The full team grid lives on
 * the About (/info) tab's Our Team accordion; this section is the teaser.
 * Self-hides when there are no members.
 */
export const ProfessionalTeamSection = ({
    sectionNumber,
}: ProfessionalTeamSectionProps = {}) => {
    const { teamMembers, projectCategories } = useLoaderData<typeof loader>();

    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    // Gate the island on the scroll-reveal so its pagination cannot show (or
    // auto-advance) while the slides are still faded out.
    const [revealed, setRevealed] = useState(false);
    // ...and on the carousel actually having somewhere to scroll: at four
    // columns a small team fits in one viewport (a single snap), where island
    // pagination would show one dot that immediately reads as ended.
    const [scrollable, setScrollable] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const update = () =>
            setScrollable(emblaApi.scrollSnapList().length > 1);
        update();
        emblaApi.on('reInit', update);
        return () => {
            emblaApi.off('reInit', update);
        };
    }, [emblaApi]);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: 'Team members',
        enabled: scrollable && revealed,
    });

    if (!teamMembers || teamMembers.length === 0) return null;

    const categoryPills = projectCategories.map(({ title, link }) => ({
        label: title,
        to: link,
    }));

    return (
        <section ref={islandRef} className="overflow-hidden bg-white">
            <EditorialHeader
                theme="light"
                eyebrow={`(${formatSectionNumber(sectionNumber ?? 2)} — Team)`}
                heading="Who we are"
                description="Meet the people behind the work — the team who bring each project to life."
                categories={
                    categoryPills.length > 0 ? categoryPills : undefined
                }
            />
            {/* animate={false}: the overview page already fades the whole
                section in — a second animated reveal here left the slides at
                opacity zero under an already-visible header. The observer (and
                the island gate it drives) still fires on schedule. */}
            <DetailScrollReveal
                delay={120}
                animate={false}
                onReveal={() => setRevealed(true)}
                className="w-full max-md:pt-8 md:pt-[3rem]"
            >
                <Carousel
                    setApi={setEmblaApi}
                    ariaLabel="Team members"
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Gutter padding on the viewport keeps the first/last snaps
                        aligned to the page gutter while the slides scroll
                        full-bleed to the viewport edge (the About-the-Supplier
                        treatment). Four columns between the gutters, with the
                        grid's 24px gap carried by per-item padding. */}
                    <CarouselContent
                        className="max-md:gap-3 md:-mx-[0.75rem]"
                        viewportClassName="max-md:px-4 md:px-[3rem] qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    >
                        {teamMembers.map((member) => (
                            <CarouselItem
                                key={member.id}
                                className="max-md:basis-[46%] md:basis-1/4 md:px-[0.75rem]"
                            >
                                <TeamMemberSlide member={member} />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </DetailScrollReveal>
        </section>
    );
};
